import { _eventBus } from "../../../../core/events/EventBus";
import { StepDefinition } from "../../../../core/flows/StepDefinition";
import { StepFlow } from "../../../../core/flows/StepFlow";
import { myAccountTree } from "../../../../Orchestrator/ConstantTree";
import { getPaymentCardsAction } from "../../../../Actions/LogicActions/MyAccount/Payments/GetPaymentCardsAction";
import { deletePaymentCardAction } from "../../../../Actions/LogicActions/MyAccount/Payments/DeletePaymentCardAction";
import { getRecoilStateValue } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { IUserResponse } from "../../../../Orchestrator/Types/Interface/IUser";
import { IDeletePaymentCardActionParams } from "../../../../Orchestrator/Types/Interface/ActionParams/PaymentCardsActionParams";
import { fetchVouchersAction } from "../../../../Actions/LogicActions/Voucher/FetchVouchersAction";
import { IAddVoucherActionParams, IAddVoucherResult, IFetchVoucherDetailActionParams, IFetchVouchersActionParams } from "../../../../Orchestrator/Types/Interface/ActionParams/VoucherActionParams";
import { fetchVoucherDetailAction, FetchVoucherDetailAction } from "../../../../Actions/LogicActions/Voucher/FetchVoucherDetailAction";
import { addVoucherAction } from "../../../../Actions/LogicActions/Voucher/AddVoucherAction";
import { getPassengerDetailsAction } from "../../../../Actions/LogicActions/Checkout/PassengerDetails/GetPassengerDetailsAction";

export class PaymentsInitStep extends StepFlow {
  get stepName() { return "MyAccountPaymentsInit_Step"; }

  async onEventSetup(): Promise<void> {
    // ProfileMenu emits MyAccount.Init when it mounts and on every token
    // change. After a hard reload the dashboard's own Init fires before the
    // token exists, so opening Payments quickly leaves MyAccountInit_Step
    // behind with no user loaded; this is the late retry. Once loaded, later
    // Inits (token refreshes) are no-ops.
    this.registerEventSubscription(
      _eventBus.on({
        eventName: myAccountTree.MyAccount.Init,
        handler: () => this.loadPaymentData(),
      })
    );

    this.registerEventSubscription(
      _eventBus.on({
        eventName: myAccountTree.paymentCards.Get,
        handler: async () => {
          const userData = getRecoilStateValue<IUserResponse>("userDataAtom");
          if (userData?.userId) {
            getPaymentCardsAction.setParams({ userId: userData.userId })
            await getPaymentCardsAction.execute();
          }
        },
      })
    );

    this.registerEventSubscription(
      _eventBus.on<IDeletePaymentCardActionParams, void>({
        eventName: myAccountTree.paymentCards.Delete,
        handler: async (payload) => {
          const { userId, paymentCardId } = payload.data!;
          if (userId && paymentCardId) {
            deletePaymentCardAction.setParams({ userId, paymentCardId });
            await deletePaymentCardAction.execute();
          }
        },
      })
    );

    this.registerEventSubscription(
      _eventBus.on({
        eventName: myAccountTree.paymentCards.Vouchers.Get,
        handler: async () => {
          const userData = getRecoilStateValue<IUserResponse>("userDataAtom");
          if (userData?.id) {
            const params: IFetchVouchersActionParams = {
              userId: userData.id
            }
            fetchVouchersAction.setParams(params)
            await fetchVouchersAction.execute()
          }
        }
      })
    )

    this.registerEventSubscription(
      _eventBus.on<IFetchVoucherDetailActionParams, void>({
        eventName: myAccountTree.paymentCards.Vouchers.Details,
        handler: async (payload) => {
          const { userId, voucherId } = payload.data!;
          fetchVoucherDetailAction.setParams({ userId, voucherId })
          await fetchVoucherDetailAction.execute()
        }
      })
    )

    this.registerEventSubscription(
      _eventBus.on<IAddVoucherActionParams, IAddVoucherResult>({
        eventName: myAccountTree.paymentCards.Vouchers.Add,
        handler: async (payload) => {
          const { userId, voucherCode } = payload.data!;
          addVoucherAction.setParams({ userId, voucherCode })
          await addVoucherAction.execute()
          payload.reply(addVoucherAction.result ?? { errorKey: "apiErrorMsg" })
        }
      })
    )

    this.registerEventSubscription(
      _eventBus.on({
        eventName: myAccountTree.exitStep,
        handler: () => {
          this.next();
        }
      })
    )

  }

  async onExecute(): Promise<void> {
    console.log("My Account - Payments Page Initialize.....");
    // Not awaited: FlowRunner holds the step's teardown (and the next tab's
    // flow) behind onExecute, so a slow /me must not delay leaving Payments.
    void this.loadPaymentData();
  }

  // One load of the user, cards and vouchers per visit (each visit gets a new
  // step instance), shared by onExecute and MyAccount.Init; cleared on failure
  // so the next Init retries.
  private paymentDataLoad: Promise<void> | null = null;

  private loadPaymentData(): Promise<void> {
    const hasToken = !!getRecoilStateValue<string>("tokenStateAtom");
    const isAnonymous = !!getRecoilStateValue<boolean>("isAnonymousStateAtom");
    if (!hasToken || isAnonymous) return Promise.resolve();

    this.paymentDataLoad ??= (async () => {
      await getPassengerDetailsAction.execute();
      if (!getPassengerDetailsAction.result) {
        this.paymentDataLoad = null;
        return;
      }
      _eventBus.emit({ eventName: myAccountTree.paymentCards.Get });
      _eventBus.emit({ eventName: myAccountTree.paymentCards.Vouchers.Get });
    })();
    return this.paymentDataLoad;
  }
}

export const PaymentsInitStepDeclaration = new StepDefinition({
  stepName: "MyAccountPaymentsInit_Step",
  stepClass: PaymentsInitStep,
})